// saveConfigurationUtil.js
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "react-query";
import { logEvent, saveConfiguration } from "../../utils/api/api";
import t from "../../utils/translation/translator";
import { getAllSteps } from "../../store/utils/utils";
import {
  selectDefaultRetrofitConfiguration,
  selectPartnerPreferences,
  setConfiguration,
  setCurrentStep
} from "../../../Retrofit/store/slices/retrofit";
import {
  selectDefaultCartesianConfiguration,
  setCartesianConfiguration,
  setCartesianCurrentStep
} from "../../../Cartesian/store/slices/cartesian";
import {
  selectDefaultMc12Configuration,
  setMc12Configuration,
  setMc12CurrentStep
} from "../../../Mc12/store/slices/mc12";
import { selectEventsData, setConfigurationType, setFullScreen } from "../../store/slices/common";
import {
  addNewIntegratedDrivesAxis,
  selectDefaultIntegratedDrivesConfiguration,
  setIntegratedDrivesConfiguration,
  setIntegratedDrivesCurrentStep
} from "../../../IntegratedDrives/store/slices/integrated_drives";
import { buildHost } from "../../utils/func.util";

export const useSaveConfiguration = (type) => {
  const dispatch = useDispatch();
  const defaultRetrofitConfiguration = useSelector(selectDefaultRetrofitConfiguration);
  const defaultCartesianConfiguration = useSelector(selectDefaultCartesianConfiguration);
  const defaultMc12Configuration = useSelector(selectDefaultMc12Configuration);
  const defaultIntegratedDrivesConfiguration = useSelector(selectDefaultIntegratedDrivesConfiguration);
  const steps = getAllSteps();
  const partnerPreferences = useSelector(selectPartnerPreferences);
  const eventsData = useSelector(selectEventsData);

  const getCurrentStep = () => {
    switch (type) {
      case "RETROFIT":
        return steps.retrofit.APPLICATION_SETTINGS;
      case "CARTESIAN":
        return steps.cartesian.KINEMATICS;
      case "MC12":
        return steps.mc12.BUILD_TRACK;
      case "INTEGRATED_DRIVES":
        return steps.integratedDrives.SPECIFY_AND_CONFIGURE;
      default:
        return steps.retrofit.APPLICATION_SETTINGS;
    }
  };

  const {
    refetch: execSave,
    isLoading: isLoadingStartConfiguration,
    isFetching: isFetchingStartConfiguration
  } = useQuery(
    "Save a new configuration",
    async () => {
      try {
        // defaultConfiguration can be either retrofit or cartesian or mc12 or others in the future
        const getDefaultConfiguration = () => {
          switch (type) {
            case "RETROFIT":
              return defaultRetrofitConfiguration;
            case "CARTESIAN":
              return defaultCartesianConfiguration;
            case "MC12":
              return defaultMc12Configuration;
            case "INTEGRATED_DRIVES":
              return defaultIntegratedDrivesConfiguration;
            default:
              return defaultRetrofitConfiguration;
          }
        };

        const defaultConfiguration = getDefaultConfiguration();
        // console.log(defaultConfiguration);
        const savedConf = await saveConfiguration({
          ...defaultConfiguration,
          configName: t("msizer.ui.my-configuration")
        });
        // console.log(savedConf);
        logEvent(
          eventsData.locale,
          eventsData.types.startConf,
          eventsData.tags.startPage,
          savedConf?.savedConfigData?.configId
        );
        let host = buildHost();
        const newConfiguration = {
          ...savedConf.savedConfigData,
          configData: {
            ...savedConf.savedConfigData.configData,
            host,
            linkToConf:
              // eslint-disable-next-line prefer-template
              window.location.href +
              (window.location.search ? "&" : "?") +
              "configuration=" +
              savedConf?.savedConfigData?.configId
          }
        };
        // console.log(newConfiguration);
        switch (type) {
          case "RETROFIT":
            dispatch(setConfiguration(newConfiguration));
            break;
          case "CARTESIAN":
            dispatch(setCartesianConfiguration(newConfiguration));
            break;
          case "MC12":
            dispatch(setMc12Configuration(newConfiguration));
            break;
          case "INTEGRATED_DRIVES":
            dispatch(setIntegratedDrivesConfiguration(newConfiguration));
            break;
          default:
            dispatch(setConfiguration(newConfiguration));
            break;
        }
        dispatch(setConfigurationType(type));

        //dispatch(setCurrentStep(steps.KINEMATICS));
        const currentStep = getCurrentStep();
        if (type === "CARTESIAN") {
          dispatch(setCartesianCurrentStep(currentStep));
        } else if (type === "MC12") {
          dispatch(setMc12CurrentStep(currentStep));
        } else if (type === "INTEGRATED_DRIVES") {
          dispatch(addNewIntegratedDrivesAxis());
          dispatch(setIntegratedDrivesCurrentStep(currentStep));
        } else {
          dispatch(setCurrentStep(currentStep));
        }

        if (partnerPreferences === null) {
          dispatch(setFullScreen(true));
          // console.log("partnerPreferences", partnerPreferences);
        }
        return savedConf;
      } catch (error) {
        console.error("ERR", error);
      }
      return undefined;
    },
    {
      enabled: false
    }
  );

  return { execSave, isLoadingStartConfiguration, isFetchingStartConfiguration };
};
